import { useNavigate } from "react-router-dom";
import { useLocale, useT } from "../../lib/i18n";
import { auth } from "../../lib/api";
import { useWS } from "../../lib/ws-context";
import NotificationBell from "./NotificationBell";
import EmergencyStopButton from "./EmergencyStopButton";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger
} from "../ui/dropdown-menu";
import { Globe, LogOut, User as UserIcon } from "lucide-react";

export default function Topbar() {
  const t = useT();
  const { locale, setLocale } = useLocale();
  const nav = useNavigate();
  const user = auth.getUser() || {};
  const tenant = auth.getTenant();
  const { status, notifTick } = useWS();
  const liveOn = status === "open";

  return (
    <header
      data-testid="app-topbar"
      className="h-16 border-b bg-background/70 backdrop-blur sticky top-0 z-30 flex items-center justify-between px-4 md:px-8"
    >
      <div className="flex items-center gap-3">
        <Badge variant="outline" data-testid="tenant-badge" className="font-mono text-xs px-2 py-1">
          {tenant}
        </Badge>
        <span className="text-xs text-muted-foreground hidden sm:inline">marketing.viona.immo</span>
        <span
          data-testid={`ws-status-${liveOn ? "open" : "closed"}`}
          title={liveOn
              ? t("ws.live", "Live")
              : t("ws.offline", "Offline — Wiederverbinden…")}
          className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wide
                          ${liveOn ? "text-emerald-600" : "text-muted-foreground"}`}
        >
          <span
            className={`inline-block h-2 w-2 rounded-full
                          ${liveOn
                              ? "bg-emerald-500 animate-pulse"
                              : "bg-slate-400"}`}
          />
          {liveOn ? t("ws.live", "Live") : t("ws.offline", "Offline")}
        </span>
      </div>
      <div className="flex items-center gap-2">
        <EmergencyStopButton />
        <NotificationBell wsTick={notifTick} />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" data-testid="language-toggle" className="gap-2">
              <Globe className="size-4" />
              <span className="uppercase font-medium text-xs">{locale}</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => setLocale("de")} data-testid="lang-de">Deutsch</DropdownMenuItem>
            <DropdownMenuItem onClick={() => setLocale("en")} data-testid="lang-en">English</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" data-testid="user-menu" className="gap-2">
              <div className="size-7 rounded-full bg-foreground text-background grid place-items-center text-xs font-semibold">
                {(user.full_name || user.email || "?").slice(0, 1).toUpperCase()}
              </div>
              <span className="text-xs hidden sm:inline max-w-[180px] truncate">{user.email}</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel className="text-xs text-muted-foreground">{user.email}</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => nav("/app/settings")} data-testid="menu-settings">
              <UserIcon className="size-4 mr-2" /> {t("nav.settings")}
            </DropdownMenuItem>
            <DropdownMenuItem
              data-testid="logout-button"
              onClick={() => {
                auth.clear();
                nav("/login");
              }}
            >
              <LogOut className="size-4 mr-2" /> {t("auth.log_out")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
